/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  Clock,
  ExternalLinkIcon,
  Info,
  MessageCircle,
  Shield,
  Users,
} from "lucide-react";
import React from "react";

interface ExternalLinkProps {
  href: string;
  children: React.ReactNode;
}

interface InfoItem {
  text: string;
  linkText?: string;
  href?: string;
  important?: boolean;
}

interface Section {
  id: string;
  title: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  items: InfoItem[];
}

const ExternalLink: React.FC<ExternalLinkProps> = ({ href, children }) => (
  <Button variant="link" className="h-auto p-0 text-blue-500" asChild>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm hover:underline"
    >
      {children}
      <ExternalLinkIcon className="h-3 w-3" />
    </a>
  </Button>
);

const SECTIONS: Section[] = [
  {
    id: "limitations",
    title: "Discord's Deletion Limits",
    description: "Understanding what Discord refuses to delete and why",
    icon: MessageCircle,
    items: [
      {
        text: "Discord refuses to delete 1:1 direct messages via privacy requests, claiming they need to preserve 'conversation context'",
        linkText: "Use DiscorchDeleter instead",
        href: "https://github.com/Darker-Ink/DiscorchDeleter",
        important: true,
      },
      {
        text: "You must leave servers and group DMs before Discord will delete messages from them",
        important: true,
      },
      {
        text: "Discord makes the deletion process deliberately difficult, trying to make you give up",
        important: true,
      },
    ],
  },
  {
    id: "license",
    title: "License & Rights",
    description: "Understanding what rights Discord claims over your content",
    icon: Shield,
    items: [
      {
        text: 'Discord claims a "worldwide, non-exclusive, royalty-free, transferable, and perpetual" license to your content',
        linkText: "Review license terms",
        href: "https://discord.com/terms#:~:text=This%20license%20is%20worldwide%2C%20non%2Dexclusive",
        important: true,
      },
      {
        text: 'They can "monitor, modify, translate, and reformat your content" without clear limitations',
        linkText: "View modification rights",
        href: "https://discord.com/terms#:~:text=Monitor%2C%20modify%2C%20translate%2C%20and%20reformat%20your%20content",
      },
      {
        text: "Discord can sublicense your content to third parties under vague terms",
        linkText: "Check sublicense details",
        href: "https://discord.com/terms#:~:text=Sublicense%20your%20content",
      },
    ],
  },
  {
    id: "retention",
    title: "Data Retention",
    description: "How long Discord keeps your data, even after deletion",
    icon: Clock,
    items: [
      {
        text: '"Deleted" messages stay in backups for 30-45 days',
        linkText: "View backup policy",
        href: "https://support.discord.com/hc/en-us/articles/5431812448791#:~:text=Our%20database%20backups",
        important: true,
      },
      {
        text: "Public messages may be kept for AI training up to 2 years after deletion",
        linkText: "Read retention policy",
        href: "https://support.discord.com/hc/en-us/articles/5431812448791#:~:text=Public%20posts%20may%20also%20be%20retained",
        important: true,
      },
      {
        text: "Reported accounts' data may be retained for up to 2 years",
        linkText: "View details",
        href: "https://support.discord.com/hc/en-us/articles/5431812448791#:~:text=If%20a%20user%20has%20been%20reported",
      },
    ],
  },
  {
    id: "sharing",
    title: "Data Sharing",
    description: "How your data might be used or shared",
    icon: Users,
    items: [
      {
        text: "Messages remain visible to others after account deletion",
        linkText: "Learn more",
        href: "https://support.discord.com/hc/en-us/articles/5431812448791#:~:text=Continuity%20of%20our%20service",
        important: true,
      },
      {
        text: "Your data may be transferred during mergers, acquisitions, or bankruptcy",
        linkText: "Review transfer terms",
        href: "https://discord.com/privacy#:~:text=Sale%2C%20Acquisition",
      },
      {
        text: "Discord has introduced targeted advertising with potential for expansion",
        linkText: "View ads policy",
        href: "https://support.discord.com/hc/en-us/articles/25516720403223-Ads-Policy",
      },
    ],
  },
];

const DataRightsSection: React.FC = () => {
  const renderSectionContent = (section: Section) => (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <section.icon className="text-muted-foreground h-4 w-4" />
          <h3 className="font-medium">{section.title}</h3>
        </div>
        <p className="text-muted-foreground text-sm">{section.description}</p>
      </div>

      <div className="space-y-3">
        {section.items.map((item, index) => (
          <Card
            key={index}
            className={
              item.important
                ? "border-yellow-500/20 bg-yellow-500/5"
                : "bg-card/50"
            }
          >
            <CardContent className="p-3">
              <div className="flex gap-2">
                {item.important && (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />
                )}
                <div className="space-y-1">
                  <p className="text-sm">{item.text}</p>
                  {item.linkText && item.href && (
                    <ExternalLink href={item.href}>
                      {item.linkText}
                    </ExternalLink>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Important Privacy Information</h2>
        <p className="text-muted-foreground text-sm">
          Review these key points about how Discord handles your data
        </p>
      </div>

      <Card className="border-red-500/20 bg-red-500/5">
        <CardHeader className="p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <CardTitle className="text-sm text-red-500">
              Critical Information!
            </CardTitle>
          </div>
          <CardDescription className="space-y-2 text-sm">
            <p>
              <strong>
                Never delete your Discord account before deleting messages.
              </strong>{" "}
              Once your account is gone, you can't delete message history.
            </p>
            <p>
              <strong>Discord refuses to delete 1:1 DMs</strong> via privacy
              requests. Use{" "}
              <ExternalLink href="https://github.com/Darker-Ink/DiscorchDeleter">
                DiscorchDeleter
              </ExternalLink>{" "}
              for those instead.
            </p>
          </CardDescription>
        </CardHeader>
      </Card>

      <Tabs defaultValue="limitations" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          {SECTIONS.map((section) => (
            <TabsTrigger key={section.id} value={section.id} className="gap-2">
              <section.icon className="h-4 w-4" />
              <span className="hidden text-sm sm:inline">{section.title}</span>
              <span className="text-sm sm:hidden">
                {section.title.split(" ")[0]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        {SECTIONS.map((section) => (
          <TabsContent key={section.id} value={section.id} className="mt-4">
            {renderSectionContent(section)}
          </TabsContent>
        ))}
      </Tabs>

      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardHeader className="p-4">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-500" />
            <CardTitle className="text-sm text-blue-500">
              Consider Signal for Private Conversations
            </CardTitle>
          </div>
          <CardDescription className="text-sm">
            For sensitive discussions, Signal offers end-to-end encryption,
            minimal data collection, and automatic message deletion.{" "}
            <ExternalLink href="https://signal.org">
              Learn more about Signal
            </ExternalLink>
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

export default DataRightsSection;
